import React, { useState, useRef, useEffect } from 'react';
import { Bot, X, Send, Loader2, GripHorizontal, Mic, MicOff } from 'lucide-react';
import { useSpeechToText } from '../hooks/useSpeechToText';
import { useChatWithAI, useAuthMe } from '../hooks/useLocalStorage';

export interface ChatMessage {
  id: string;
  sender: 'user' | 'bot';
  text: string;
  timestamp: Date;
  mainUrl?: { obj: string; val: string };
  otherUrl?: { obj: string; val: string }[];
}

export default function AIAssistant() {
  const { data: user } = useAuthMe();
  const [isOpen, setIsOpen] = useState(false);
  const [isDocked, setIsDocked] = useState(false);
  const [dockWidth, setDockWidth] = useState(384);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputText, setInputText] = useState('');
  const [sessionId, setSessionId] = useState<string>('');
  const [isTyping, setIsTyping] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const chatMutation = useChatWithAI();

  // Speech-to-text
  const {
    isListening,
    interimTranscript,
    isSupported: isSpeechSupported,
    error: speechError,
    toggleListening,
    stopListening: stopSpeech,
  } = useSpeechToText({
    lang: 'en-US',
    continuous: false,
    interimResults: true,
    onResult: (finalText) => {
      setInputText(prev => {
        const separator = prev.trim() ? ' ' : '';
        return prev + separator + finalText;
      });
    },
  });

  // Show speech errors as a transient state
  const [speechErrorVisible, setSpeechErrorVisible] = useState(false);
  useEffect(() => {
    if (speechError) {
      setSpeechErrorVisible(true);
      const timer = setTimeout(() => setSpeechErrorVisible(false), 4000);
      return () => clearTimeout(timer);
    }
  }, [speechError]);

  // Dragging state
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStartPos = useRef({ x: 0, y: 0 });
  const windowPos = useRef({ x: 0, y: 0 });

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const shouldAutoScroll = useRef(true);

  // Dock Resizing
  const [isResizingDock, setIsResizingDock] = useState(false);

  useEffect(() => {
    const handleDockResizeMove = (e: MouseEvent) => {
      if (!isResizingDock) return;
      const newWidth = window.innerWidth - e.clientX;
      // Enforce min width of 280 and max width of 800
      setDockWidth(Math.max(280, Math.min(newWidth, 800)));
    };

    const handleDockResizeEnd = () => {
      setIsResizingDock(false);
    };

    if (isResizingDock) {
      document.body.style.cursor = 'ew-resize';
      document.body.style.userSelect = 'none';
      window.addEventListener('mousemove', handleDockResizeMove);
      window.addEventListener('mouseup', handleDockResizeEnd);
    } else {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', handleDockResizeMove);
      window.removeEventListener('mouseup', handleDockResizeEnd);
    }

    return () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', handleDockResizeMove);
      window.removeEventListener('mouseup', handleDockResizeEnd);
    };
  }, [isResizingDock]);

  const hasInitialized = useRef(false);

  const parseMessagesArray = (messagesArr: any[], prefix: string = 'init') => {
    const newHistory: ChatMessage[] = [];
    messagesArr.forEach((m: any, idx: number) => {
      if (m.message) {
        newHistory.push({
          id: Date.now().toString() + `-${prefix}-user-${idx}`,
          sender: 'user',
          text: m.message,
          timestamp: new Date()
        });
      }
      if (m.assistantReply) {
        newHistory.push({
          id: Date.now().toString() + `-${prefix}-bot-${idx}`,
          sender: 'bot',
          text: m.assistantReply,
          timestamp: new Date(),
          mainUrl: m.mainUrl,
          otherUrl: m.otherUrl
        });
      }
    });
    return newHistory;
  };

  // Initialize greeting via API
  useEffect(() => {
    if (!user || (user as any).workspace_mode === 'without-ai') return;

    if (messages.length === 0 && !hasInitialized.current) {
      hasInitialized.current = true;

      const fetchInitialGreeting = async () => {
        setIsTyping(true);
        try {
          const response = await chatMutation.mutateAsync({ message: "", sessionId: "", microservice: "rulemanager", page: 1 });
          if (response.sessionId) {
            setSessionId(response.sessionId);
          }

          if (response.messages && response.messages.length > 0) {
            const parsedMessages = parseMessagesArray(response.messages);
            if ((response as any).mainUrl || (response as any).otherUrl) {
              for (let i = parsedMessages.length - 1; i >= 0; i--) {
                if (parsedMessages[i].sender === 'bot') {
                  parsedMessages[i].mainUrl = (response as any).mainUrl;
                  parsedMessages[i].otherUrl = (response as any).otherUrl;
                  break;
                }
              }
            }
            setMessages(parsedMessages);
            shouldAutoScroll.current = true;
          } else {
            const botMessage: ChatMessage = {
              id: Date.now().toString(),
              sender: 'bot',
              text: response.assistantReply,
              timestamp: new Date(),
              mainUrl: (response as any).mainUrl,
              otherUrl: (response as any).otherUrl
            };
            setMessages([botMessage]);
          }
        } catch (error: any) {
          console.error("Failed to fetch initial greeting", error);
          let errorText = "Hello, I am having trouble connecting to the server. How can I help?";
          if (error?.response?.data?.message) {
            errorText = error.response.data.message;
          } else if (error instanceof Error) {
            try {
              const jsonStr = error.message.substring(error.message.indexOf('{'));
              if (jsonStr) {
                const parsed = JSON.parse(jsonStr);
                if (parsed.message) errorText = parsed.message;
              }
            } catch (e) {}
          }
          setMessages([{
            id: Date.now().toString(),
            sender: 'bot',
            text: errorText,
            timestamp: new Date(),
          }]);
        } finally {
          setIsTyping(false);
        }
      };

      fetchInitialGreeting();
    }
  }, [messages.length, chatMutation, user]);

  // Reset auto-scroll when opened
  useEffect(() => {
    if (isOpen) {
      shouldAutoScroll.current = true;
    }
  }, [isOpen]);

  // Scroll to bottom on new message or when opened
  useEffect(() => {
    if (shouldAutoScroll.current && isOpen) {
      setTimeout(() => {
        if (messagesContainerRef.current) {
          messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
        }
      }, 50);
    }
  }, [messages, isTyping, isOpen]);

  const handleScroll = async (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    if (target.scrollTop === 0 && hasMore && !isLoadingMore && messages.length > 0) {
      setIsLoadingMore(true);
      const previousScrollHeight = target.scrollHeight;
      try {
        const nextPage = page + 1;
        const response = await chatMutation.mutateAsync({ message: "", sessionId, microservice: "rulemanager", page: nextPage });

        if (response.messages && response.messages.length > 0) {
          const newHistory = parseMessagesArray(response.messages, `page${nextPage}`);

          shouldAutoScroll.current = false;
          setMessages(newHistory);
          setPage(nextPage);

          if (response.hasMore !== undefined) {
            setHasMore(response.hasMore);
          } else if (response.messages.length <= messages.length) {
            setHasMore(false);
          }

          setTimeout(() => {
            if (messagesContainerRef.current) {
              const newScrollHeight = messagesContainerRef.current.scrollHeight;
              messagesContainerRef.current.scrollTop = newScrollHeight - previousScrollHeight;
            }
          }, 0);
        } else {
          setHasMore(false);
        }
      } catch (error) {
        console.error("Failed to load more messages", error);
      } finally {
        setIsLoadingMore(false);
      }
    }
  };

  const handleDragStart = (e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
    if (isDocked) return; // Don't drag if docked

    // Don't drag if clicking buttons
    if ((e.target as HTMLElement).closest('button')) return;

    setIsDragging(true);

    let clientX, clientY;
    if ('touches' in e) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }

    dragStartPos.current = { x: clientX, y: clientY };
    windowPos.current = { ...position };
  };

  const handleDragMove = (e: MouseEvent | TouchEvent) => {
    if (!isDragging) return;

    let clientX, clientY;
    if ('touches' in e) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }

    const deltaX = clientX - dragStartPos.current.x;
    const deltaY = clientY - dragStartPos.current.y;

    setPosition({
      x: windowPos.current.x + deltaX,
      y: windowPos.current.y + deltaY
    });
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleDragMove);
      window.addEventListener('mouseup', handleDragEnd);
      window.addEventListener('touchmove', handleDragMove, { passive: false });
      window.addEventListener('touchend', handleDragEnd);
    } else {
      window.removeEventListener('mousemove', handleDragMove);
      window.removeEventListener('mouseup', handleDragEnd);
      window.removeEventListener('touchmove', handleDragMove);
      window.removeEventListener('touchend', handleDragEnd);
    }

    return () => {
      window.removeEventListener('mousemove', handleDragMove);
      window.removeEventListener('mouseup', handleDragEnd);
      window.removeEventListener('touchmove', handleDragMove);
      window.removeEventListener('touchend', handleDragEnd);
    };
  }, [isDragging]);

  const handleSendMessage = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!inputText.trim()) return;

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      sender: 'user',
      text: inputText.trim(),
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInputText('');
    setIsTyping(true);
    shouldAutoScroll.current = true;
    setPage(1); // Reset page to 1 on new message
    setHasMore(true);

    try {
      const response = await chatMutation.mutateAsync({ message: userMessage.text, sessionId, microservice: "rulemanager", page: 1 });
      if (response.sessionId) {
        setSessionId(response.sessionId);
      }

      if (response.messages && response.messages.length > 0) {
        const newHistory = parseMessagesArray(response.messages, 'send');

        // Robustness: ensure the user's latest message isn't dropped if backend omitted it
        const hasUserMessage = newHistory.some(m => m.sender === 'user' && m.text === userMessage.text);
        if (!hasUserMessage) {
          const lastBotMsg = newHistory.length > 0 && newHistory[newHistory.length - 1].sender === 'bot'
            ? newHistory.pop()
            : null;

          newHistory.push(userMessage);
          if (lastBotMsg) newHistory.push(lastBotMsg);
        }

        if ((response as any).mainUrl || (response as any).otherUrl) {
          for (let i = newHistory.length - 1; i >= 0; i--) {
            if (newHistory[i].sender === 'bot') {
              newHistory[i].mainUrl = (response as any).mainUrl;
              newHistory[i].otherUrl = (response as any).otherUrl;
              break;
            }
          }
        }

        setMessages(newHistory);
      } else {
        const botMessage: ChatMessage = {
          id: (Date.now() + 1).toString(),
          sender: 'bot',
          text: response.assistantReply,
          timestamp: new Date(),
          mainUrl: (response as any).mainUrl,
          otherUrl: (response as any).otherUrl
        };
        setMessages(prev => [...prev, botMessage]);
      }
    } catch (error: any) {
      console.error("Failed to fetch AI response", error);
      let errorText = "I'm having trouble connecting right now. Please try again later.";
      if (error?.response?.data?.message) {
        errorText = error.response.data.message;
      } else if (error instanceof Error) {
        try {
          const jsonStr = error.message.substring(error.message.indexOf('{'));
          if (jsonStr) {
            const parsed = JSON.parse(jsonStr);
            if (parsed.message) errorText = parsed.message;
          }
        } catch (e) {}
      }
      const errorMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        sender: 'bot',
        text: errorText,
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
    }
  };

  if ((user as any)?.workspace_mode === 'without-ai') {
    return null;
  }

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 z-50 p-4 bg-primary text-primary-foreground rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 flex items-center justify-center"
        style={{ backgroundColor: '#2563eb', color: 'white' }}
        aria-label="Open AI Assistant"
      >
        <Bot size={28} />
      </button>
    );
  }

  // Styles change significantly based on whether it is docked or floating
  const containerStyle = isDocked
    ? {
      width: `${dockWidth}px`,
      height: 'auto',
      alignSelf: 'stretch',
      borderLeft: '1px solid hsl(var(--border))',
      borderRadius: '0px',
      resize: 'none' as const,
      flexShrink: 0,
      transition: isResizingDock ? 'none' : 'width 0.3s ease',
    }
    : {
      bottom: position.y === 0 ? '24px' : 'auto',
      right: position.x === 0 ? '24px' : 'auto',
      top: position.y !== 0 ? `calc(100vh - 450px + ${position.y}px)` : 'auto',
      left: position.x !== 0 ? `calc(100vw - 384px + ${position.x}px)` : 'auto',
      width: '384px',
      height: '450px',
      minWidth: '280px',
      minHeight: '350px',
      maxWidth: '90vw',
      maxHeight: '90vh',
      resize: 'both' as const,
      transition: isDragging ? 'none' : 'all 0.1s ease',
    };

  return (
    <div
      className={`${isDocked ? 'relative min-h-0' : 'fixed border rounded-2xl'
        } z-50 flex flex-col bg-background shadow-2xl overflow-hidden`}
      style={containerStyle}
    >
      {/* Dock Resize Handle */}
      {isDocked && (
        <div
          className="absolute left-0 top-0 bottom-0 w-1.5 hover:w-2 hover:bg-blue-500/20 cursor-ew-resize z-10 transition-all flex items-center justify-center group"
          onMouseDown={(e) => {
            e.preventDefault();
            setIsResizingDock(true);
          }}
        >
          <div className="h-8 w-1 rounded-full bg-slate-300 dark:bg-slate-600 opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
      )}

      {/* Header */}
      <div
        className={`flex items-center justify-between p-3 border-b bg-muted/30 ${isDocked ? '' : 'cursor-grab active:cursor-grabbing'
          }`}
        onMouseDown={handleDragStart}
        onTouchStart={handleDragStart}
      >
        <div className="flex items-center gap-2">
          <div className="p-1.5 bg-blue-100 dark:bg-blue-900 rounded-full text-blue-600 dark:text-blue-400">
            <Bot size={20} />
          </div>
          <div>
            <h3 className="text-sm font-semibold">AI Assistant</h3>
            <p className="text-xs text-muted-foreground">Always here to help</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setIsDocked(!isDocked)}
            className={`p-1 text-muted-foreground hover:bg-muted rounded-md transition-colors ${isDocked ? 'bg-muted text-primary' : ''
              }`}
            title={isDocked ? "Float window" : "Dock window"}
          >
            <GripHorizontal size={18} />
          </button>
          <button
            onClick={() => setIsOpen(false)}
            className="p-1 text-muted-foreground hover:bg-muted rounded-md hover:text-red-500 transition-colors"
            aria-label="Close chat"
          >
            <X size={18} />
          </button>
        </div>
      </div>

      {/* Messages Area */}
      <div
        ref={messagesContainerRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50 dark:bg-slate-900/50"
      >
        {isLoadingMore && (
          <div className="flex justify-center py-2">
            <Loader2 size={16} className="animate-spin text-blue-500" />
          </div>
        )}
        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm shadow-sm ${msg.sender === 'user'
                ? 'bg-blue-600 text-white rounded-tr-sm'
                : 'bg-white dark:bg-slate-800 border rounded-tl-sm text-slate-800 dark:text-slate-200'
                }`}
            >
              <p className="leading-relaxed whitespace-pre-wrap">{msg.text}</p>
              
              {msg.sender === 'bot' && msg.mainUrl && (
                <div className="mt-4">
                  <a href={msg.mainUrl.val} className="block text-center bg-blue-500 hover:bg-blue-600 text-white text-xs font-semibold py-2 px-4 rounded-lg transition-colors">
                    {msg.mainUrl.obj}
                  </a>

                  {msg.otherUrl && msg.otherUrl.length > 0 && (
                    <div className="mt-3 text-center">
                      <span className="text-[10px] text-slate-500 block mb-2">(u can also explore)</span>
                      <div className="flex flex-wrap gap-2 justify-center">
                        {msg.otherUrl.map((link, i) => (
                          <a key={i} href={link.val} className="bg-slate-200 hover:bg-slate-300 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 text-xs font-semibold py-2 px-4 rounded-lg transition-colors">
                            {link.obj}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}

        {isTyping && (
          <div className="flex justify-start">
            <div className="bg-white dark:bg-slate-800 border rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm flex items-center gap-2">
              <Loader2 size={16} className="animate-spin text-blue-500" />
              <span className="text-xs text-slate-500 font-medium">Assistant is typing...</span>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="p-3 bg-background border-t">

        {/* Speech error banner */}
        {speechErrorVisible && speechError && (
          <div className="mb-2 px-3 py-1.5 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-xs text-red-600 dark:text-red-400 flex items-center gap-2">
            <MicOff size={12} />
            <span>{speechError}</span>
          </div>
        )}
        {/* Listening indicator */}
        {isListening && (
          <div className="mb-2 px-3 py-1.5 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg text-xs text-blue-600 dark:text-blue-400 flex items-center gap-2">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
            </span>
            <span>{interimTranscript || 'Listening...'}</span>
          </div>
        )}
        <form
          onSubmit={handleSendMessage}
          className="flex items-center gap-2 bg-muted/40 p-1 rounded-full border focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500 transition-all"
        >
          <input
            type="text"
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            placeholder={isListening ? "Listening..." : "Type a message..."}
            className="flex-1 bg-transparent border-none focus:outline-none px-3 py-1.5 text-sm"
          />
          {isSpeechSupported && (
            <button
              type="button"
              onClick={toggleListening}
              className={"p-2 rounded-full transition-all shrink-0 " + (isListening ? 'bg-red-500 text-white hover:bg-red-600 animate-pulse' : 'bg-blue-100 text-blue-600 hover:bg-blue-200 hover:text-blue-700 dark:bg-blue-900/50 dark:text-blue-400 dark:hover:bg-blue-800/60')}
              title={isListening ? 'Stop listening' : 'Start listening'}
            >
              {isListening ? <MicOff size={18} /> : <Mic size={18} />}
            </button>
          )}
          <button
            type="submit"
            disabled={!inputText.trim() || chatMutation.isPending}
            className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shrink-0"
            aria-label="Send message"
          >
            <Send size={16} className="ml-0.5" />
          </button>
        </form>
      </div>
    </div>
  );
}
